// What /vendor is to somebody who does not sell here yet: the merchant
// portal's front page, a marketing page for the other side of the counter.
//
// What it has to say: Aldoo is not a replacement for a merchant's own site —
// it is another channel that brings them sales they would not have had; the
// commission is from just 7% to 25% — by the catalog of products, the exact
// rate set after the review and written into the contract — and one sale at
// 10% shows what that commission buys; and getting there is three steps (register, import the
// catalogue, sell).
//
// The hero's products are photos made with Gemini (public/static/landing); the
// example sale shows the cover of a book on sale now, from the catalogue. The dashboards are examples with sample data (sales in thousands
// in the store's currency, the rest worked out from them) and say so.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import PubSub from "pubsub-js"
import {
  ArrowRightIcon,
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  PaperAirplaneIcon,
  BanknotesIcon,
  BuildingStorefrontIcon,
  CreditCardIcon,
  MegaphoneIcon,
  WrenchScrewdriverIcon,
  ChartBarIcon,
  CheckIcon,
  CircleStackIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  InboxArrowDownIcon,
  MagnifyingGlassIcon,
  ReceiptPercentIcon,
  ShoppingBagIcon,
  SparklesIcon,
  TruckIcon,
  UserPlusIcon,
  ArrowDownTrayIcon,
  ArrowLeftOnRectangleIcon,
  CalendarIcon,
  CheckBadgeIcon,
  Cog6ToothIcon,
  CubeIcon,
  TagIcon,
  PlusIcon,
  DevicePhoneMobileIcon,
  BookOpenIcon,
  Squares2X2Icon,
  UserGroupIcon,
  UserIcon,
} from "@heroicons/react/24/outline"
import { Area, AreaChart, Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { animate, motion, useAnimationFrame, useReducedMotion } from "framer-motion"
import usePortalTexts from "./texts/usePortalTexts"
import { LANDING } from "./texts/bundles"
import useSiteLanguage from "../Storefront/hooks/useSiteLanguage"
import { usePageContext } from "vike-react/usePageContext"
import { currencySymbol, useStoreCurrency } from "aldoo-ra/Store/currency"
import { ALDOO_ADVISOR } from "../AdminShared/advisor-brand"
import { ShippingCarriers } from "../Storefront/storeApi"
import { url as cmsUrl } from "aldoo-ra/CMS/url"

// the storefront palette (pages/Layout.css), for the charts' SVG
const PRIMARY = "#D25C28"
const ACCENT = "#07567A"
const YELLOW = "#FCDA7D"

// the width every section's content is held to
const CONTENT_WIDTH = "max-w-[1200px]"

// ---------------------------------------------------------------- sample data
// Sales per month in thousands, in the store's currency; everything else is
// worked out from it.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
const SALES_K = [18.4, 20.9, 23.6, 22.8, 27.5, 30.9, 29.4, 33.8, 38.1, 42.6, 47.3, 54.2]
const AVERAGE_ORDER = 24.6 // in the store's currency
const CONVERSION = 3.4 // % of visits that buy
const REPEAT = 31 // % of orders from returning buyers
const CHANNELS = [
  { name: "Website", share: 58, color: PRIMARY },
  { name: "iOS app", share: 24, color: ACCENT },
  { name: "Android app", share: 18, color: YELLOW },
]
// the top products' shares of the month's sales: the shop's best sellers, with sample shares
const PRODUCT_SHARES = [9, 7, 6, 5, 4]

const sampleFigures = () => {
  const year = SALES_K.reduce((a, b) => a + b, 0)
  const last = SALES_K[SALES_K.length - 1]
  const before = SALES_K[SALES_K.length - 2]
  const orders = Math.round((last * 1000) / AVERAGE_ORDER)
  return {
    year,
    last,
    growth: ((last - before) / before) * 100,
    orders,
    visits: Math.round(orders / (CONVERSION / 100)),
    series: MONTHS.map((month, i) => ({ month, sales: SALES_K[i] })),
  }
}

const moneyK = (symbol, value, digits = 1) => `${symbol}${value.toFixed(digits)}K`

/**
 * The symbol of the store's currency (STORE_CURRENCY) the sample figures are
 * written in. The server hands it with the page (pageContext.storeCurrency),
 * so the first render already has it.
 */
function useMoneySymbol() {
  const fromPage = usePageContext()?.storeCurrency?.currency
  const { currency } = useStoreCurrency()
  return currencySymbol(fromPage || currency, "en")
}
const thousands = (value) => value.toLocaleString("en-US")

/** Charts draw in the browser only: the server has no width to lay them out in. */
const useMounted = () => {
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])
  return mounted
}



// ---------------------------------------------------------------- the advisor's chat
// An example conversation with Aldoo AI on the same sample figures. The
// products are made up: sample sales are not put on real books.
const TOP_PRODUCTS = [
  { name: "Illustrated children's atlas", sold: 412, revenue: 6.8 },
  { name: "Family cookbook", sold: 286, revenue: 5.1 },
  { name: "Fairy tales audiobook", sold: 198, revenue: 4.3 },
]
const WEEKDAYS = [
  { day: "Mon", share: 12 },
  { day: "Tue", share: 11 },
  { day: "Wed", share: 12 },
  { day: "Thu", share: 13 },
  { day: "Fri", share: 14 },
  { day: "Sat", share: 18 },
  { day: "Sun", share: 20 },
]
// who says what, and how long before it (ms): the advisor "types" before each answer
const SCRIPT = [
  { who: "user", kind: "askTop", wait: 500 },
  { who: "ai", kind: "top", wait: 1400 },
  { who: "user", kind: "askDays", wait: 1300 },
  { who: "ai", kind: "days", wait: 1500 },
  { who: "ai", kind: "tip", wait: 1100 },
]

/** The chat plays once it is on screen (whole, at once, for reduced motion); `replay` starts it over. */
function useChatPlayback(ref) {
  const [shown, setShown] = useState(0)
  const [typing, setTyping] = useState(false)
  const [run, setRun] = useState(0)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === "undefined") return setVisible(true)
    const observer = new IntersectionObserver(([entry]) => entry.isIntersecting && setVisible(true), { threshold: 0.35 })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  useEffect(() => {
    if (!visible) return
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setShown(SCRIPT.length)
      return
    }
    setShown(0)
    const timers = []
    let at = 0
    SCRIPT.forEach((step, i) => {
      at += step.wait
      if (step.who === "ai") timers.push(setTimeout(() => setTyping(true), at - Math.min(900, step.wait - 200)))
      timers.push(
        setTimeout(() => {
          setTyping(false)
          setShown(i + 1)
        }, at)
      )
    })
    return () => timers.forEach(clearTimeout)
  }, [visible, run])
  return { shown, typing, done: shown >= SCRIPT.length, replay: () => setRun((n) => n + 1) }
}

function AdvisorChat({ t }) {
  const symbol = useMoneySymbol()
  const ref = useRef(null)
  const bodyRef = useRef(null)
  const { shown, typing, done, replay } = useChatPlayback(ref)
  // the newest message in view, as a chat keeps it
  useEffect(() => {
    const body = bodyRef.current
    if (body) body.scrollTo({ top: body.scrollHeight, behavior: "smooth" })
  }, [shown, typing])

  const bubble = (step, i) => {
    if (step.who === "user") {
      const text = step.kind === "askTop" ? t`Which products sold best last month?` : t`And which days of the week sell the most?`
      return (
        <div key={i} className="flex justify-end landing-chat-in">
          <div className="max-w-[80%] px-4 py-2.5 rounded-2xl rounded-br-md bg-primary text-white text-[14px] leading-snug">{text}</div>
        </div>
      )
    }
    let content = null
    if (step.kind === "top")
      content = (
        <>
          <p className="m-0 mb-3">{t`Your three best sellers last month, by sales:`}</p>
          <ol className="m-0 p-0 list-none flex flex-col gap-2">
            {TOP_PRODUCTS.map((product, n) => (
              <li key={product.name} className="flex items-center gap-3 p-2.5 rounded-xl bg-paper border border-bordered">
                <span className={`w-7 h-7 shrink-0 rounded-lg flex items-center justify-center text-[13px] font-bold ${n === 0 ? "bg-primary text-white" : "bg-neutral text-text"}`}>{n + 1}</span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] font-semibold truncate">{t(product.name)}</span>
                  <span className="block text-[11px] text-text_secondary">{t("%s sold", product.sold)}</span>
                </span>
                <span className="text-[14px] font-bold tabular-nums">{moneyK(symbol, product.revenue)}</span>
              </li>
            ))}
          </ol>
          <p className="m-0 mt-3">{t`Together they are 30% of the month — the atlas alone grew 22% on the month before.`}</p>
        </>
      )
    if (step.kind === "days")
      content = (
        <>
          <p className="m-0 mb-3">{t`Weekends are your strongest days: Saturday and Sunday bring 38% of your sales.`}</p>
          <div className="flex items-end gap-1.5 h-[92px] px-1">
            {WEEKDAYS.map(({ day, share }) => (
              <div key={day} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[10px] text-text_secondary tabular-nums">{share}%</span>
                <span className="w-full rounded-t-md" style={{ height: `${(share / 20) * 56}px`, background: share >= 18 ? PRIMARY : "#e9b8a2" }} />
                <span className="text-[10px] text-text_secondary">{t(day)}</span>
              </div>
            ))}
          </div>
        </>
      )
    if (step.kind === "tip")
      content = (
        <p className="m-0">
          <b>{t`Tip:`}</b> {t`start your promotions on Friday, so they run through the weekend — and keep the atlas in stock for Saturday.`}
        </p>
      )
    return (
      <div key={i} className="flex items-end gap-2 landing-chat-in">
        <img src={ALDOO_ADVISOR.icon} alt="" className="w-7 h-7 shrink-0 rounded-full bg-dark object-contain p-1" />
        <div className="max-w-[85%] px-4 py-3 rounded-2xl rounded-bl-md bg-neutral text-text text-[14px] leading-snug">{content}</div>
      </div>
    )
  }

  return (
    <div ref={ref} className="relative rounded-3xl bg-paper border border-bordered shadow-[0_24px_60px_rgba(0,0,0,0.14)] overflow-hidden" data-testid="landing-advisor-chat">
      <style>{`@keyframes landing-chat-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}.landing-chat-in{animation:landing-chat-in .35s ease-out both}@keyframes landing-dot{0%,80%,100%{opacity:.25;transform:translateY(0)}40%{opacity:1;transform:translateY(-3px)}}.landing-dot{animation:landing-dot 1.2s infinite}`}</style>
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-bordered">
        <img src={ALDOO_ADVISOR.icon} alt="" className="w-8 h-8 rounded-full bg-dark object-contain p-1" />
        <div className="flex-1 min-w-0">
          <span className="block text-[14px] font-bold">{ALDOO_ADVISOR.name}</span>
          <span className="block text-[11px] text-text_secondary">{typing ? t`typing…` : t`Knows your sales, orders and buyers`}</span>
        </div>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-neutral text-text_secondary">{t`Sample data`}</span>
      </div>
      <div ref={bodyRef} className="h-[440px] md:h-[470px] overflow-y-auto px-4 md:px-5 py-5 flex flex-col gap-3 scrollbar-none">
        {SCRIPT.slice(0, shown).map(bubble)}
        {typing && (
          <div className="flex items-end gap-2 landing-chat-in" data-testid="landing-chat-typing">
            <img src={ALDOO_ADVISOR.icon} alt="" className="w-7 h-7 shrink-0 rounded-full bg-dark object-contain p-1" />
            <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-neutral flex gap-1">
              {[0, 1, 2].map((n) => (
                <span key={n} className="landing-dot w-1.5 h-1.5 rounded-full bg-text_secondary" style={{ animationDelay: `${n * 0.15}s` }} />
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 px-4 py-3 border-t border-bordered">
        <span className="flex-1 h-10 px-4 rounded-full bg-neutral text-[13px] text-text_secondary flex items-center truncate">{t`Ask about your sales…`}</span>
        {done ? (
          <button type="button" onClick={replay} className="w-10 h-10 rounded-full bg-dark text-white flex items-center justify-center" title={t`Play again`} data-testid="landing-chat-replay">
            <ArrowUturnLeftIcon className="w-5 h-5" />
          </button>
        ) : (
          <span className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center"><PaperAirplaneIcon className="w-5 h-5" /></span>
        )}
      </div>
    </div>
  )
}


// ---------------------------------------------------------------- the portal, as a merchant sees it
// An example company's merchant portal on its Orders screen, drawn as the
// real one is: the menu, the order statuses (which filter the list), the
// search, and a page of sample orders. The company and its buyers are made
// up; the emails are example.com.
const PORTAL_MENU = [
  [ShoppingBagIcon, "Orders"],
  [ChartBarIcon, "Sales"],
  [CubeIcon, "Products"],
  [Squares2X2Icon, "Dashboard"],
  [BuildingStorefrontIcon, "Company"],
  [Cog6ToothIcon, "Settings"],
  [TruckIcon, "Couriers"],
  [UserGroupIcon, "Members"],
  [BanknotesIcon, "Payouts"],
  [ArrowDownTrayIcon, "Import"],
]
const ORDER_STATUSES = [
  { id: "open", label: "Open", dot: "#3b82f6", count: 3 },
  { id: "shipping", label: "In delivery", dot: "#f59e0b", count: 5 },
  { id: "done", label: "Fulfilled", dot: "#22c55e", count: 128 },
  { id: "cancelled", label: "Cancelled", dot: "#ef4444", count: 2 },
  { id: "partly", label: "Partly refunded", dot: "#b45309", count: 1 },
  { id: "refunded", label: "Refunded", dot: "#a855f7", count: 4 },
]
// the sample orders, made to match the tiles: each status has as many as its tile says, up to a page of them
// (Fulfilled: a full page of its 128), newest first; the names in the reader's language
const PORTAL_PAGE = 8
const PORTAL_TOTAL = ORDER_STATUSES.reduce((sum, s) => sum + s.count, 0)
const SAMPLE_PEOPLE = [
  ["Maria Petrova", "Мария Петрова", "m.petrova"], ["Georgi Ivanov", "Георги Иванов", "g.ivanov"], ["Elena Dimitrova", "Елена Димитрова", "elena.d"],
  ["Nikolay Stoyanov", "Николай Стоянов", "n.stoyanov"], ["Iva Koleva", "Ива Колева", "iva.koleva"], ["Petar Nikolov", "Петър Николов", "p.nikolov"],
  ["Desislava Georgieva", "Десислава Георгиева", "desi.g"], ["Stefan Todorov", "Стефан Тодоров", "s.todorov"], ["Viktoria Angelova", "Виктория Ангелова", "v.angelova"],
  ["Hristo Marinov", "Христо Маринов", "h.marinov"], ["Radostina Pavlova", "Радостина Павлова", "rado.p"], ["Kaloyan Dimov", "Калоян Димов", "k.dimov"],
  ["Teodora Ilieva", "Теодора Илиева", "teodora.i"], ["Martin Kostov", "Мартин Костов", "m.kostov"], ["Gergana Vasileva", "Гергана Василева", "g.vasileva"],
  ["Boris Atanasov", "Борис Атанасов", "b.atanasov"], ["Silvia Hristova", "Силвия Христова", "silvia.h"], ["Yordan Petkov", "Йордан Петков", "y.petkov"],
  ["Nadezhda Ruseva", "Надежда Русева", "n.ruseva"], ["Lyubomir Genchev", "Любомир Генчев", "l.genchev"], ["Polina Stoeva", "Полина Стоева", "polina.s"],
  ["Emil Bonev", "Емил Бонев", "e.bonev"], ["Ralitsa Mihaylova", "Ралица Михайлова", "ralitsa.m"],
]
// how long ago each status's orders were placed (hours): the open ones today, the fulfilled ones over the weeks
const STATUS_AGES = { open: [2, 5, 26], shipping: [27, 49, 52, 70, 75], done: [80, 98, 120, 145, 170, 196, 220, 250], cancelled: [30, 110], partly: [160], refunded: [125, 190, 240, 300] }
const TODAY = new Date(Date.UTC(2026, 8, 30, 12))
const SAMPLE_ORDERS = (() => {
  const out = []
  let person = 0
  for (const s of ORDER_STATUSES) {
    STATUS_AGES[s.id].slice(0, Math.min(s.count, PORTAL_PAGE)).forEach((hours, i) => {
      const [en, bg, mail] = SAMPLE_PEOPLE[person++ % SAMPLE_PEOPLE.length]
      const seed = (person * 37 + i * 11) % 97
      const total = Math.round((12 + seed * 0.83) * 10) / 10
      const placed = new Date(TODAY.getTime() - hours * 3600 * 1000)
      out.push({
        id: (0x4f2a9c70 - hours * 13717 - i * 31).toString(16).toUpperCase(),
        name: { en, bg },
        email: `${mail}@example.com`,
        phone: `+359 8${7 + (seed % 3)} *** ${String(1000 + ((seed * 113) % 9000)).slice(0, 4)}`,
        hours,
        date: `${String(placed.getUTCDate()).padStart(2, "0")}.${String(placed.getUTCMonth() + 1).padStart(2, "0")}.${placed.getUTCFullYear()}`,
        items: 1 + (seed % 5),
        total,
        ...(seed % 4 === 0 ? { discount: Math.round(total * 0.1 * 100) / 100 } : {}),
        status: s.id,
      })
    })
  }
  return out.sort((a, b) => a.hours - b.hours)
})()
// "2 h ago", "yesterday", "3 days ago"
const agoOf = (hours, t) => (hours < 24 ? t("%s h ago", hours) : hours < 48 ? t`yesterday` : t("%s days ago", Math.floor(hours / 24)))
const STATUS_CHIP = { open: "bg-[#e8eefc] text-[#2f55d4]", shipping: "bg-[#fdf3e1] text-[#a86b00]", done: "bg-[#e7f7ec] text-[#1f8a4c]", cancelled: "bg-[#fde8e8] text-[#c53030]", partly: "bg-[#f6ede4] text-[#8a4b12]", refunded: "bg-[#f3e8fd] text-[#7e3fb8]" }

/**
 * The portal picture leaning toward the pointer, and what is under the pointer popping out of it — the parts
 * marked `data-pop` lift and grow as the pointer nears them (Aldoo Meet's site, TiltWrapper and its showcase).
 * Nothing moves for a touch screen, or for those who ask for less motion.
 */
function PopTilt({ children, maxX = 4, maxY = 6 }) {
  const still = useReducedMotion()
  const box = useRef(null)
  const plate = useRef(null)
  const frame = useRef(null)
  const pointer = useRef({ x: 0, y: 0, in: false })
  const tilt = useRef({ x: 2, y: -3, tx: 2, ty: -3 })
  const lifts = useRef(new Map())
  const step = useCallback(() => {
    const p = pointer.current
    const k = tilt.current
    k.x += (k.tx - k.x) * 0.08
    k.y += (k.ty - k.y) * 0.08
    if (plate.current) plate.current.style.transform = `rotateY(${k.y.toFixed(2)}deg) rotateX(${k.x.toFixed(2)}deg)`
    let settling = Math.abs(k.tx - k.x) > 0.02 || Math.abs(k.ty - k.y) > 0.02
    for (const el of box.current ? box.current.querySelectorAll("[data-pop]") : []) {
      let target = 0
      if (p.in) {
        const r = el.getBoundingClientRect()
        const nx = Math.max(r.left, Math.min(r.right, p.x))
        const ny = Math.max(r.top, Math.min(r.bottom, p.y))
        target = Math.max(0, 1 - Math.hypot(p.x - nx, p.y - ny) / 90)
      }
      const now = lifts.current.get(el) || 0
      const next = now + (target - now) * 0.15
      lifts.current.set(el, next)
      if (Math.abs(target - next) > 0.005) settling = true
      el.style.transform = next > 0.002 ? `translateY(${(-next * 4).toFixed(2)}px) scale(${(1 + next * 0.045).toFixed(4)})` : ""
      el.style.boxShadow = next > 0.002 ? `0 ${(next * 14).toFixed(1)}px ${(next * 28).toFixed(1)}px rgba(15,23,42,${(next * 0.16).toFixed(3)})` : ""
      el.style.position = "relative"
      el.style.zIndex = next > 0.05 ? "2" : ""
    }
    frame.current = p.in || settling ? requestAnimationFrame(step) : null
  }, [])
  useEffect(() => () => frame.current && cancelAnimationFrame(frame.current), [])
  const run = () => {
    if (!frame.current) frame.current = requestAnimationFrame(step)
  }
  if (still) return children
  return (
    <div
      ref={box}
      style={{ perspective: "1600px" }}
      onMouseMove={(e) => {
        const r = box.current.getBoundingClientRect()
        pointer.current = { x: e.clientX, y: e.clientY, in: true }
        tilt.current.tx = -((e.clientY - (r.top + r.height / 2)) / (r.height / 2)) * maxX
        tilt.current.ty = ((e.clientX - (r.left + r.width / 2)) / (r.width / 2)) * maxY
        run()
      }}
      onMouseLeave={() => {
        pointer.current.in = false
        tilt.current.tx = 2
        tilt.current.ty = -3
        run()
      }}
      data-testid="landing-portal-tilt"
    >
      <div ref={plate} style={{ transform: "rotateY(-3deg) rotateX(2deg)", transformStyle: "preserve-3d", willChange: "transform" }}>
        {children}
      </div>
    </div>
  )
}

function PortalPreview({ t }) {
  const symbol = useMoneySymbol()
  const [status, setStatus] = useState(null)
  const [query, setQuery] = useState("")
  const labelOf = (id) => t(ORDER_STATUSES.find((s) => s.id === id)?.label || id)
  const language = useSiteLanguage()
  const nameOf = (order) => (language === "bg" ? order.name.bg : order.name.en)
  const matches = SAMPLE_ORDERS.filter((order) => (!status || order.status === status) && (!query || `${order.id} ${nameOf(order)} ${order.email}`.toLowerCase().includes(query.toLowerCase())))
  const rows = matches.slice(0, PORTAL_PAGE)
  // what the list is a page of: the tile's count (all of them unfiltered), or what the search found
  const of = query ? matches.length : status ? ORDER_STATUSES.find((s) => s.id === status).count : PORTAL_TOTAL
  return (
    <div className="rounded-2xl md:rounded-3xl bg-[#e9ecf1] p-2 md:p-3 shadow-[0_30px_80px_rgba(0,0,0,0.18)]" data-testid="landing-portal">
      {/* the browser around it */}
      <div className="flex items-center gap-2 px-2 pb-2 md:pb-3">
        <span className="flex gap-1.5">{["#ff5f57", "#febc2e", "#28c840"].map((c) => <span key={c} className="w-3 h-3 rounded-full" style={{ background: c }} />)}</span>
        <span className="flex-1 max-w-[420px] mx-auto h-7 px-3 rounded-lg bg-white/80 text-[12px] text-text_secondary flex items-center justify-center truncate">aldoo.com/vendor</span>
        <span className="w-12" />
      </div>
      <div className="rounded-xl md:rounded-2xl bg-white flex text-left" style={{ fontSize: 13 }}>
        {/* the menu */}
        <aside className="hidden md:flex w-[220px] shrink-0 flex-col border-r border-[#eef0f3] px-3 py-5">
          <div className="flex items-center gap-3 px-2 mb-1 rounded-xl bg-white" data-pop>
            <span className="w-11 h-11 rounded-full bg-gradient-to-br from-[#0f4c5c] to-[#2c7a7b] text-white font-bold flex items-center justify-center">LB</span>
            <span className="min-w-0">
              <span className="flex items-center gap-1 text-[15px] font-bold text-text">Lumen Books <CheckBadgeIcon className="w-4 h-4 text-[#3b82f6]" /></span>
              <span className="block text-[12px] text-text_secondary">lumen-books</span>
            </span>
          </div>
          <span className="px-2 mb-4 text-[12px] text-[#3b82f6]">{t`Switch organization`}</span>
          <nav className="flex flex-col gap-0.5">
            {PORTAL_MENU.map(([Icon, label], i) => (
              <span key={label} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${i === 0 ? "bg-[#eaf2fe] text-[#2f6fdb] font-semibold" : "text-text bg-white"}`} data-pop>
                <Icon className="w-5 h-5" />
                <span className="flex-1">{t(label)}</span>
                {i === 0 && <span className="w-5 h-5 rounded-full bg-[#3b82f6] text-white text-[11px] font-bold flex items-center justify-center">3</span>}
              </span>
            ))}
          </nav>
          <span className="mt-auto pt-4 border-t border-[#eef0f3] flex items-center gap-3 px-3 py-2.5 text-text"><ArrowLeftOnRectangleIcon className="w-5 h-5" /> {t`Sign out`}</span>
        </aside>

        {/* the Orders screen */}
        <div className="flex-1 min-w-0 p-3 md:p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <span className="text-[16px] md:text-[17px] font-semibold text-text">{t`Orders`}</span>
            <span className="h-9 px-3 rounded-lg border border-[#e3e6ea] bg-white text-[12px] md:text-[13px] text-text flex items-center gap-2" data-pop><CalendarIcon className="w-4 h-4" /> {t`Last 30 days`}</span>
          </div>
          <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
            {ORDER_STATUSES.map((s) => {
              const active = status === s.id
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setStatus(active ? null : s.id)}
                  className={`text-left px-3 py-2.5 rounded-xl border transition-colors ${active ? "bg-[#4a72d4] border-[#4a72d4] text-white" : "bg-white border-[#e3e6ea] hover:border-[#b9c7ee]"}`}
                  data-testid="landing-portal-status"
                  data-pop
                >
                  <span className="flex items-center gap-1.5 text-[11px] md:text-[12px] leading-tight min-h-[28px]">
                    <span className="w-2 h-2 shrink-0 rounded-full" style={{ background: active ? "#fff" : s.dot }} />
                    {t(s.label)}
                  </span>
                  <span className="block text-[20px] md:text-[22px] font-bold tabular-nums">{s.count}</span>
                </button>
              )
            })}
          </div>
          <label className="flex items-center gap-2 h-10 px-3 mb-3 rounded-lg bg-[#f4f5f7] text-text_secondary" data-pop>
            <MagnifyingGlassIcon className="w-4 h-4 shrink-0" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t`Order number, email, phone, product, tracking number…`} className="flex-1 min-w-0 bg-transparent outline-none text-[13px] text-text" data-testid="landing-portal-search" />
          </label>
          <div className="overflow-x-auto md:overflow-visible">
            <table className="w-full min-w-[620px] border-collapse">
              <thead>
                <tr className="text-left text-[12px] md:text-[13px] font-semibold text-text border-b border-[#eef0f3]">
                  <th className="py-2.5 pr-3 font-semibold">{t`Order`}</th>
                  <th className="py-2.5 pr-3 font-semibold">{t`Customer`}</th>
                  <th className="py-2.5 pr-3 font-semibold">{t`Placed`}</th>
                  <th className="py-2.5 pr-3 font-semibold text-right">{t`Items`}</th>
                  <th className="py-2.5 pr-3 font-semibold text-right">{t`Total`}</th>
                  <th className="py-2.5 font-semibold">{t`Status`}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((order, i) => (
                  <tr key={order.id} className={`align-middle ${i % 2 ? "bg-[#f7f8fa]" : "bg-white"}`} data-testid="landing-portal-row" data-pop>
                    <td className="py-2.5 pr-3 font-semibold tabular-nums">{order.id}</td>
                    <td className="py-2.5 pr-3">
                      <span className="block text-text">{nameOf(order)}</span>
                      <span className="block text-[11px] text-text_secondary">{order.email}</span>
                      <span className="block text-[11px] text-text_secondary">{order.phone}</span>
                    </td>
                    <td className="py-2.5 pr-3">
                      <span className="block tabular-nums">{order.date}</span>
                      <span className="block text-[11px] text-text_secondary">{agoOf(order.hours, t)}</span>
                    </td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">{order.items}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums">
                      <span className="block font-semibold">{symbol}{order.total.toFixed(2)}</span>
                      {order.discount && <span className="block text-[11px] text-text_secondary">−{symbol}{order.discount.toFixed(2)} {t`discount`}</span>}
                    </td>
                    <td className="py-2.5"><span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${STATUS_CHIP[order.status]}`}>{labelOf(order.status)}</span></td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr><td colSpan={6} className="py-8 text-center text-text_secondary">{t`No orders match`}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-3 text-[12px] text-text_secondary">
            <span>{t("%s of %s shown", rows.length, of)}</span>
            <span className="flex gap-2">
              <span className="w-8 h-8 rounded-full border border-[#e3e6ea] flex items-center justify-center opacity-40"><ArrowRightIcon className="w-4 h-4 rotate-180" /></span>
              <span className="w-8 h-8 rounded-full border border-[#e3e6ea] flex items-center justify-center"><ArrowRightIcon className="w-4 h-4" /></span>
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- pieces

function Kpi({ label, value, note, tone = "text-text" }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-[11px] uppercase tracking-wide text-text_secondary leading-tight">{label}</span>
      <span className={`text-[20px] md:text-[22px] font-bold leading-tight tabular-nums ${tone}`}>{value}</span>
      {note && <span className="text-[11px] text-text_secondary leading-tight">{note}</span>}
    </div>
  )
}

// the hero's products: one of each kind a shop may sell here (photos made with Gemini, public/static/landing/hero)
const HERO_PRODUCTS = [
  { name: "Lemonade", photo: "/static/landing/hero/beverage.webp" },
  { name: "Specialty coffee", photo: "/static/landing/hero/coffee.webp" },
  { name: "Sneakers", photo: "/static/landing/hero/shoes.webp" },
  { name: "Smartwatch", photo: "/static/landing/hero/smartwatch.webp" },
  { name: "Football", photo: "/static/landing/hero/sports.webp" },
  { name: "Sunglasses", photo: "/static/landing/hero/glasses.webp" },
  { name: "Knit sweater", photo: "/static/landing/hero/clothes.webp" },
]
const HERO_BOOKS = HERO_PRODUCTS.length

/**
 * The hero's picture: a fan of products of every kind over a dashboard of
 * sample sales. The products spring in one after another, and with each one
 * the figures and the chart grow by its share — the story of the page: every
 * product you bring adds to what you sell. Shown finished at once for reduced motion.
 */
function HeroVisual({ figures, t, mounted }) {
  const symbol = useMoneySymbol()
  const reduceMotion = useReducedMotion()
  const [landed, setLanded] = useState(0)
  // started once, and left to finish
  const timers = useRef(null)
  useEffect(() => {
    if (!mounted || timers.current) return
    if (reduceMotion) {
      timers.current = []
      return setLanded(HERO_BOOKS)
    }
    timers.current = Array.from({ length: HERO_BOOKS }, (_, i) => setTimeout(() => setLanded(i + 1), 250 + i * 380))
  }, [mounted, reduceMotion])
  useEffect(() => () => (timers.current || []).forEach(clearTimeout), [])

  // what the books so far have brought in: their share of the finished
  // figures, sprung towards as each one lands — the numbers and the chart
  // are drawn from the one value, so they grow together and always finish
  const [share, setShare] = useState(0)
  const shareRef = useRef(0)
  useEffect(() => {
    const target = landed / HERO_BOOKS
    if (reduceMotion) return setShare(target)
    const controls = animate(shareRef.current, target, {
      type: "spring",
      stiffness: 110,
      damping: 20,
      onUpdate: (v) => {
        shareRef.current = v
        setShare(v)
      },
    })
    return () => controls.stop()
  }, [landed, reduceMotion])
  const series = useMemo(() => figures.series.map((point) => ({ ...point, sales: +(point.sales * Math.max(0, share)).toFixed(2) })), [figures, share])
  const done = landed >= HERO_BOOKS

  return (
    // taller on a phone: the card keeps its height on a narrow screen, so in a 5:4 box it
    // covered the books; at 4:5 they stand above it
    <div className="relative w-full max-w-[560px] mx-auto aspect-[4/5] sm:aspect-[5/4]" data-testid="landing-hero-visual">
      {/* the products, fanned behind the dashboard */}
      <div className="absolute inset-0">
        {HERO_PRODUCTS.map((product, i) => {
          const middle = (HERO_BOOKS - 1) / 2
          const rotate = (i - middle) * 5
          const shown = i < landed
          return (
            <motion.div
              key={product.name}
              // larger on a phone (the 4:5 box leaves room above the card), spread evenly across either way
              className="absolute [--w:31%] sm:[--w:25%] w-[var(--w)] aspect-square rounded-2xl overflow-hidden bg-[#f1efec] shadow-[0_12px_30px_rgba(0,0,0,0.14)]"
              style={{ left: `calc((100% - var(--w)) * ${i / (HERO_BOOKS - 1)})`, top: `${i % 2 ? 3 : 10}%`, zIndex: 10 - Math.round(Math.abs(i - middle)) }}
              initial={false}
              animate={shown ? { opacity: 1, y: 0, scale: 1, rotate } : { opacity: 0, y: -90, scale: 0.55, rotate: rotate - 24 }}
              transition={{ type: "spring", stiffness: 260, damping: 16, mass: 0.9 }}
              data-testid="landing-hero-book"
              data-landed={shown ? "1" : "0"}
            >
              <img src={product.photo} alt={t(product.name)} className="w-full h-full object-cover" draggable={false} />
            </motion.div>
          )
        })}
      </div>
      {/* the dashboard card */}
      <div className="absolute left-[4%] right-[4%] bottom-0 z-10 rounded-2xl bg-paper shadow-[0_24px_60px_rgba(0,0,0,0.22)] border border-bordered p-4 md:p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[13px] font-semibold text-text">{t`Your sales on Aldoo`}</span>
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-neutral text-text_secondary">{t`Sample data`}</span>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-3" data-testid="landing-hero-kpis">
          <Kpi
            label={t`This month`}
            value={moneyK(symbol, Math.max(0, figures.last * share))}
            note={
              <motion.span className="inline-block" initial={false} animate={done ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.6 }} transition={{ type: "spring", stiffness: 400, damping: 14 }}>
                ▲ {figures.growth.toFixed(1)}%
              </motion.span>
            }
            tone="text-primary"
          />
          <Kpi label={t`Orders`} value={thousands(Math.max(0, Math.round(figures.orders * share)))} note={`${symbol}${AVERAGE_ORDER.toFixed(2)} ${t`average order`}`} />
          <Kpi label={t`12 months`} value={moneyK(symbol, Math.max(0, figures.year * share), 0)} note={t`in new sales`} />
        </div>
        <div className="h-[110px] md:h-[130px] -mx-1">
          {mounted && (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                <defs>
                  <linearGradient id="landing-hero-fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={PRIMARY} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={PRIMARY} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                {/* the scale is the finished one, so the area grows into it */}
                <YAxis hide domain={[0, Math.ceil(Math.max(...SALES_K) * 1.08)]} />
                <XAxis dataKey="month" tickFormatter={(m) => t(m)} tick={{ fontSize: 10, fill: "#6e6e6e" }} axisLine={false} tickLine={false} interval={1} />
                <Tooltip formatter={(v) => [moneyK(symbol, v), t`Sales`]} labelFormatter={(m) => t(m)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Area type="monotone" dataKey="sales" stroke={PRIMARY} strokeWidth={2.5} fill="url(#landing-hero-fill)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  )
}

// The "a channel, not a replacement" flow, as a moving picture: shoppers come to Aldoo, Aldoo recommends the
// company's products to them, and each customer goes on to the company's shop carrying the product they chose
// and what they pay (+€…) — the customers come to the company, not its products to Aldoo. Still for less motion.
// simple outline drawings for what heroicons has no icon of: a shirt, a skirt, headphones, a lipstick, a jar of cream
const line = (d) =>
  function Drawn({ className = "", style }) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className={className} style={style} aria-hidden="true">
        {d.map((path) => (
          <path key={path} d={path} />
        ))}
      </svg>
    )
  }
const ShirtIcon = line(["M8.5 3.5 4 6l1.8 3.6L8 8.6V20.5h8V8.6l2.2 1L20 6l-4.5-2.5c-.4 1.4-1.8 2.4-3.5 2.4s-3.1-1-3.5-2.4Z"])
const SkirtIcon = line(["M8 4h8l.8 3H7.2L8 4Z", "M7.2 7 4 20h16L16.8 7"])
const HeadphonesIcon = line(["M4 15v-3a8 8 0 0 1 16 0v3", "M4 15h3v5H5a1 1 0 0 1-1-1v-4Z", "M20 15h-3v5h2a1 1 0 0 0 1-1v-4Z"])
const LipstickIcon = line(["M9 21h6v-8H9v8Z", "M10 13V8l4-3.5V13"])

// what the customers carry to the company: clothes, electronics, books and cosmetics
const FLOW_PRODUCTS = [
  // `photo`: a product photo (made with Gemini, public/static/landing/products) where a picture shows it
  { icon: ShirtIcon, color: "#2563EB", name: "Men's shirt", photo: "/static/landing/products/shirt.webp" },
  { icon: HeadphonesIcon, color: "#0F172A", name: "Wireless headphones", photo: "/static/landing/products/headphones.webp" },
  { icon: BookOpenIcon, color: "#D25C28", name: "Bestseller novel", photo: "/static/landing/products/book.webp" },
  { icon: LipstickIcon, color: "#DB2777", name: "Lipstick", photo: "/static/landing/products/lipstick.webp" },
  { icon: SkirtIcon, color: "#1D4ED8", name: "Blue skirt", photo: "/static/landing/products/skirt.webp" },
  { icon: DevicePhoneMobileIcon, color: "#475569", name: "Smartphone", photo: "/static/landing/products/phone.webp" },
  { icon: ShoppingBagIcon, color: "#D25C28", name: "Specialty coffee", photo: "/static/landing/hero/coffee.webp" },
  { icon: ShoppingBagIcon, color: "#0F172A", name: "Sneakers", photo: "/static/landing/hero/shoes.webp" },
  { icon: ShoppingBagIcon, color: "#0F172A", name: "Smartwatch", photo: "/static/landing/hero/smartwatch.webp" },
  { icon: ShoppingBagIcon, color: "#0F172A", name: "Sunglasses", photo: "/static/landing/hero/glasses.webp" },
]
const FLOW_SECONDS = 6.5
const randomAmount = () => 5 + Math.floor(Math.random() * 96)

/** A node of the flow: its icon (or `mark`, a picture) in a circle (`tone`), a title and one line under it. */
function FlowNode({ icon: Icon, mark = null, title, body, tone = "plain", size = "md", children }) {
  const big = size === "lg"
  const ring = tone === "dark" ? "bg-dark text-white shadow-[0_24px_60px_rgba(0,0,0,0.25)]" : "bg-paper text-text shadow-[0_2px_24px_rgba(0,0,0,0.06)]"
  return (
    <div className="relative flex flex-col items-center text-center gap-3 w-full md:w-[210px] shrink-0">
      <div className={`relative ${big ? "w-32 h-32 md:w-36 md:h-36" : "w-28 h-28"} rounded-full flex items-center justify-center ${ring}`}>
        {mark || <Icon className={big ? "w-14 h-14 text-primary" : "w-11 h-11"} />}
        {children}
      </div>
      <Typography component="h3" variant="h4" className="m-0">
        {title}
      </Typography>
      <Typography variant="txt1_dim" className="block leading-snug max-w-[260px]">
        {body}
      </Typography>
    </div>
  )
}

// the shoppers' colours, one drawn for each
const SHOPPER_COLORS = ["#2563EB", "#D25C28", "#2F9E5B", "#7C3AED", "#DB2777", "#0F172A"]

/** A shopper on the way to Aldoo: a face, nothing chosen yet. */
function ShopperChip({ color }) {
  return (
    <span className="flex items-center justify-center w-9 h-9 rounded-full bg-paper border border-bordered shadow-md">
      <UserIcon className="w-5 h-5" style={{ color }} />
    </span>
  )
}

/** A customer Aldoo sends on to the company: the face, the product they chose beside it, what they pay above. */
function CustomerChip({ color, product, amount, symbol }) {
  return (
    <span className="relative flex flex-col items-center">
      <span className="absolute -top-6 px-1.5 py-0.5 rounded-md bg-[#2F9E5B] text-white text-[11px] font-bold tabular-nums whitespace-nowrap shadow-sm">
        +{symbol}{amount}
      </span>
      <span className="relative flex items-end">
        <span className="flex items-center justify-center w-9 h-9 rounded-full bg-paper border border-bordered shadow-md">
          <UserIcon className="w-5 h-5" style={{ color }} />
        </span>
        <span className="-ml-2.5 -mb-1.5 block w-7 h-7 rounded-lg overflow-hidden bg-[#f1efec] shadow-md ring-2 ring-paper">
          <img src={product.photo} alt="" className="w-full h-full object-cover" draggable={false} />
        </span>
      </span>
    </span>
  )
}

/** Wide enough for the flow across (md): one lane is drawn, so each arrival counts once. */
function useWide() {
  const [wide, setWide] = useState(true)
  useEffect(() => {
    const query = window.matchMedia("(min-width: 768px)")
    const update = () => setWide(query.matches)
    update()
    query.addEventListener("change", update)
    return () => query.removeEventListener("change", update)
  }, [])
  return wide
}

const TRAVELLERS = 3

/**
 * A lane: its dashes flowing the way things travel on it, and its travellers (still: set along it). One clock
 * moves them all, a third of a trip apart, so they never bunch up; each starts again as a new one at the far
 * end (`render` gets its number, `onArrive` hears each arrival).
 */
function FlowLane({ vertical, reverse, still, render, onArrive, className = "" }) {
  const dashes = still ? {} : { animate: { strokeDashoffset: reverse ? [0, 16] : [0, -16] }, transition: { duration: 1.8, repeat: Infinity, ease: "linear" } }
  const axis = vertical ? "top" : "left"
  const place = `absolute ${vertical ? "left-1/2 -translate-x-1/2 -translate-y-1/2" : "top-1/2 -translate-y-1/2 -translate-x-1/2"}`
  const [trips, setTrips] = useState(() => Array(TRAVELLERS).fill(0))
  const tripsRef = useRef(Array(TRAVELLERS).fill(0))
  const travellers = useRef([])
  useAnimationFrame((time) => {
    if (still) return
    const clock = time / 1000 / FLOW_SECONDS
    let changed = false
    for (let i = 0; i < TRAVELLERS; i++) {
      const el = travellers.current[i]
      if (!el) continue
      // the i-th sets off a third of a trip after the one before it
      const run = clock - i / TRAVELLERS
      if (run < 0) {
        el.style.opacity = "0"
        continue
      }
      const trip = Math.floor(run)
      if (trip !== tripsRef.current[i]) {
        onArrive?.(i * 1000 + tripsRef.current[i])
        tripsRef.current[i] = trip
        changed = true
      }
      const along = run - trip
      el.style[axis] = `${(reverse ? 1 - along : along) * 100}%`
      el.style.opacity = String(along < 0.1 ? along / 0.1 : along > 0.9 ? (1 - along) / 0.1 : 1)
    }
    if (changed) setTrips([...tripsRef.current])
  })
  return (
    <div className={`relative ${className}`} aria-hidden="true">
      <svg className={vertical ? "absolute inset-y-0 left-1/2 -translate-x-1/2 h-full w-2 overflow-visible" : "absolute inset-x-0 top-1/2 -translate-y-1/2 w-full h-2 overflow-visible"} preserveAspectRatio="none" viewBox={vertical ? "0 0 2 100" : "0 0 100 2"}>
        <motion.line {...(vertical ? { x1: 1, y1: 0, x2: 1, y2: 100 } : { x1: 0, y1: 1, x2: 100, y2: 1 })} stroke="#C8CDD3" strokeWidth="2" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" {...dashes} />
      </svg>
      {still
        ? [0, 1].map((i) => (
            <span key={i} className={place} style={{ [axis]: `${33 + i * 34}%` }}>
              {render(i)}
            </span>
          ))
        : trips.map((trip, i) => (
            <span key={i} ref={(el) => (travellers.current[i] = el)} className={place} style={{ opacity: 0 }}>
              {render(i * 1000 + trip)}
            </span>
          ))}
    </div>
  )
}

function ChannelFlow({ t }) {
  const still = useReducedMotion()
  const symbol = useMoneySymbol()
  const wide = useWide()
  // each traveller's draw (its colour, product, amount), made when it sets off and kept for its trip
  const draws = useRef(new Map())
  // the products drawn last: the next is none of them (no product twice on the lane, nor one after another)
  const recent = useRef([])
  const drawOf = (key) => {
    if (!draws.current.has(key)) {
      // a shopper ("s…") carries nothing yet: only a customer's draw takes a product
      let product = null
      if (key[0] === "c") {
        const free = FLOW_PRODUCTS.map((_, index) => index).filter((index) => !recent.current.includes(index))
        product = free[Math.floor(Math.random() * free.length)]
        recent.current = [...recent.current, product].slice(-TRAVELLERS)
      }
      draws.current.set(key, { color: SHOPPER_COLORS[Math.floor(Math.random() * SHOPPER_COLORS.length)], product: product === null ? null : FLOW_PRODUCTS[product], amount: randomAmount() })
    }
    return draws.current.get(key)
  }
  const shopper = (n) => <ShopperChip color={drawOf(`s${n}`).color} />
  const customer = (n) => {
    const draw = drawOf(`c${n}`)
    return <CustomerChip color={draw.color} product={draw.product} amount={draw.amount} symbol={symbol} />
  }
  // what the customers brought the company so far: each one's amount added as they arrive
  const [earned, setEarned] = useState(1240)
  const arrived = (n) => {
    const amount = draws.current.get(`c${n}`)?.amount || 0
    setEarned((total) => (total + amount > 99999 ? 1240 : total + amount))
  }
  // a lane between two nodes: across on a wide screen, down on a phone, its name above
  const lane = (label, render, onArrive) => (
    <div className="flex md:flex-1 flex-col items-center md:items-stretch justify-center md:mt-3 md:min-w-[120px]">
      <span className="text-[12px] font-bold uppercase tracking-wide text-text text-center">{label}</span>
      <FlowLane key={wide ? "across" : "down"} vertical={!wide} still={still} render={render} onArrive={onArrive} className={wide ? "w-full h-12 mt-5" : "w-12 h-40 mt-8"} />
    </div>
  )
  return (
    <div className="mt-14 md:mt-20" data-testid="landing-channel-flow">
      <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-6 md:gap-3">
        {/* where the customers come from: the people shopping on Aldoo */}
        <FlowNode icon={UserGroupIcon} title={t`Shoppers`} body={t`People who search and browse Aldoo every day.`} />

        {lane(t`Shoppers`, shopper)}

        {/* Aldoo, its logo in the circle: recommends the company's products to them — a ring turning, a pulse */}
        <FlowNode
          mark={
            <picture className="relative z-[1] flex">
              <source srcSet="/static/p-images/aldoo--277x58.webp" type="image/webp" />
              <img src="/static/p-images/aldoo--162x33.jpg" alt="Aldoo" width={277} height={58} className="w-[92px] md:w-[104px] h-auto" draggable={false} />
            </picture>
          }
          title={t`Aldoo Marketplace`} body={t`Recommends your products to the shoppers who want them.`} tone="dark" size="lg">
          {!still && <motion.span className="absolute inset-0 rounded-full bg-primary/30" animate={{ scale: [1, 1.25], opacity: [0.5, 0] }} transition={{ duration: 3.5, repeat: Infinity, ease: "easeOut" }} aria-hidden="true" />}
          <motion.svg className="absolute -inset-3 w-[calc(100%+24px)] h-[calc(100%+24px)]" viewBox="0 0 100 100" animate={still ? undefined : { rotate: 360 }} transition={{ duration: 32, repeat: Infinity, ease: "linear" }} aria-hidden="true">
            <circle cx="50" cy="50" r="48" fill="none" stroke="#D25C28" strokeWidth="1.2" strokeDasharray="3 5" />
          </motion.svg>
        </FlowNode>

        {lane(t`Customers to you`, customer, arrived)}

        {/* the company: the customers arrive here with what they chose, and the money with them */}
        <FlowNode icon={BuildingStorefrontIcon} title={t`Your shop`} body={t`The customer buys from you: the order and the money come to you.`}>
          <motion.span key={earned} className="absolute -top-2 left-1/2 ml-4 h-[30px] px-2.5 rounded-full bg-[#2F9E5B] text-white text-sm font-bold flex items-center justify-center shadow-md tabular-nums whitespace-nowrap" initial={still ? false : { scale: 0.7 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 400, damping: 20 }}>
            {symbol}{thousands(earned)}
          </motion.span>
          {!still && <motion.span key={`ping${earned}`} className="absolute inset-0 rounded-full border-2 border-[#2F9E5B]" initial={{ scale: 1, opacity: 0.5 }} animate={{ scale: 1.4, opacity: 0 }} transition={{ duration: 1.6, ease: "easeOut" }} aria-hidden="true" />}
        </FlowNode>
      </div>
    </div>
  )
}

/**
 * The catalogue import as a moving picture: Aldoo visits the company's shop (a magnifier going over its products
 * one by one), each product it finds travels over and lands in Aldoo Marketplace, then it starts again. The
 * company types in nothing. Everything imported at once for those who ask for less motion.
 */
const IMPORT_COUNT = 6
const IMPORT_STEP_MS = 1100
function ImportVisit({ t }) {
  const still = useReducedMotion()
  // the product being visited now (IMPORT_COUNT and over: all done, a pause before it starts again)
  const [step, setStep] = useState(still ? IMPORT_COUNT : 0)
  useEffect(() => {
    if (still) return
    const timer = setInterval(() => setStep((n) => (n >= IMPORT_COUNT + 2 ? 0 : n + 1)), IMPORT_STEP_MS)
    return () => clearInterval(timer)
  }, [still])
  const items = FLOW_PRODUCTS.slice(0, IMPORT_COUNT)
  // a product's thumbnail: its photo, as a shop shows it
  const tile = (item, extra = "") => (
    <span className={`block aspect-square rounded-xl border overflow-hidden bg-[#f1efec] ${extra}`}>
      <img src={item.photo} alt={t(item.name)} className="w-full h-full object-cover" loading="lazy" draggable={false} />
    </span>
  )
  return (
    <div className="mt-10 grid grid-cols-1 [&>*]:min-w-0 md:grid-cols-[1fr_120px_1fr] gap-4 md:gap-2 items-center" data-testid="landing-import-visit">
      {/* the company's shop, visited */}
      <div className="rounded-2xl bg-paper border border-bordered shadow-md overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-bordered bg-neutral_1">
          <span className="flex gap-1">{["#ff5f57", "#febc2e", "#28c840"].map((c) => <span key={c} className="w-2.5 h-2.5 rounded-full" style={{ background: c }} />)}</span>
          <span className="flex-1 text-center text-[12px] text-text_secondary truncate">{t`yourshop.com`}</span>
        </div>
        <div className="p-4 grid grid-cols-3 gap-3">
          {items.map((item, i) => (
            <span key={item.name} className="relative">
              {tile(item, i === step ? "border-primary ring-2 ring-primary/40 bg-primary/5" : "border-bordered bg-white")}
              {/* the magnifier, over the product being read */}
              {i === step && !still && (
                <motion.span className="absolute -right-2 -bottom-2 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center shadow-md" initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.25 }}>
                  <MagnifyingGlassIcon className="w-4 h-4" />
                </motion.span>
              )}
              {/* read already */}
              {i < step && (
                <span className="absolute -right-1.5 -top-1.5 w-5 h-5 rounded-full bg-[#2F9E5B] text-white flex items-center justify-center shadow-sm">
                  <CheckIcon className="w-3 h-3" strokeWidth={3} />
                </span>
              )}
            </span>
          ))}
        </div>
        <div className="px-4 pb-4 text-center text-[13px] font-bold text-text">{t`Your shop`}</div>
      </div>

      {/* the product on its way */}
      <div className="relative h-14 md:h-24" aria-hidden="true">
        <svg className="absolute inset-x-0 top-1/2 -translate-y-1/2 w-full h-2 overflow-visible hidden md:block" preserveAspectRatio="none" viewBox="0 0 100 2">
          <line x1="0" y1="1" x2="100" y2="1" stroke="#C8CDD3" strokeWidth="2" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
        </svg>
        {step < IMPORT_COUNT && !still ? (
          <motion.span
            key={step}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 hidden md:block"
            initial={{ left: "0%", opacity: 0 }}
            animate={{ left: ["0%", "100%"], opacity: [0, 1, 1, 0] }}
            transition={{ duration: IMPORT_STEP_MS / 1000, delay: 0.15, ease: "easeInOut", times: [0, 0.2, 0.8, 1] }}
          >
            {tile(items[step], "border-bordered bg-white shadow-md")}
          </motion.span>
        ) : null}
        <span className="md:hidden absolute inset-0 flex items-center justify-center text-primary">
          <ArrowRightIcon className="w-6 h-6 rotate-90" />
        </span>
      </div>

      {/* Aldoo Marketplace, filling up */}
      <div className="rounded-2xl bg-dark text-white shadow-[0_20px_50px_rgba(0,0,0,0.2)] overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
          <BuildingStorefrontIcon className="w-4 h-4 text-primary" />
          <span className="text-[12px] font-bold">{t`Aldoo Marketplace`}</span>
        </div>
        <div className="p-4 grid grid-cols-3 gap-3">
          {items.map((item, i) =>
            i < step ? (
              <motion.span key={`${item.name}-${step >= IMPORT_COUNT ? "all" : "in"}`} initial={still ? false : { scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 380, damping: 20 }}>
                {tile(item, "border-white/10 bg-white")}
              </motion.span>
            ) : (
              <span key={item.name} className="aspect-square rounded-xl border-2 border-dashed border-white/15" />
            )
          )}
        </div>
        <div className="px-4 pb-4 text-center text-[13px] font-bold">
          {Math.min(step, IMPORT_COUNT)} / {IMPORT_COUNT} {t`imported`}
        </div>
      </div>
    </div>
  )
}

/**
 * A parcel being packed, on a loop: a product drops into an open box, the flaps close and are taped, and the
 * waybill is stamped on its side — what a connected courier account does at each order. Drawn in SVG
 * (isometric), timed with framer-motion; a packed, labelled box for those who ask for less motion.
 */
const BOX_SECONDS = 7
function PackingBox() {
  const still = useReducedMotion()
  // every part runs on the same clock: keyframes at shares of BOX_SECONDS
  // (no `initial: false` while moving: with it framer-motion shows the last keyframe and plays nothing)
  const timed = (values, times, rest) => (still ? { initial: false, animate: rest } : { initial: Object.fromEntries(Object.entries(values).map(([key, frames]) => [key, frames[0]])), animate: values, transition: { duration: BOX_SECONDS, times, repeat: Infinity, ease: "easeInOut" } })
  return (
    <svg viewBox="0 0 240 230" className="w-[200px] md:w-[240px] h-auto" role="img" aria-label="" data-testid="landing-packing-box">
      {/* the shadow under the box */}
      <ellipse cx="120" cy="214" rx="86" ry="12" fill="#000" opacity="0.08" />
      {/* inside the open box */}
      <polygon points="120,60 200,100 120,140 40,100" fill="#7A4E26" />
      {/* the back flaps, standing open behind the product until they close */}
      <motion.g {...timed({ opacity: [1, 1, 0, 0, 1] }, [0, 0.24, 0.32, 0.95, 1], { opacity: 0 })}>
        <polygon points="120,60 200,100 214,72 134,32" fill="#B98247" />
        <polygon points="120,60 40,100 26,72 106,32" fill="#CF9A60" />
      </motion.g>
      {/* the product, dropping in */}
      <motion.g {...timed({ y: [-80, -80, 40, 40, 40], opacity: [0, 1, 1, 0, 0] }, [0, 0.04, 0.2, 0.36, 1], { y: 40, opacity: 0 })}>
        <rect x="100" y="40" width="40" height="46" rx="5" fill="#fff" stroke="#E5E7EB" strokeWidth="2" />
        <rect x="108" y="48" width="24" height="30" rx="3" fill="#D25C28" />
        <path d="M113 56h14M113 62h14M113 68h9" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
      </motion.g>
      {/* the two front faces (in front of the product) */}
      <polygon points="40,100 120,140 120,210 40,170" fill="#C8935A" />
      <polygon points="120,140 200,100 200,170 120,210" fill="#B07A42" />
      <path d="M40,100 L120,140 L200,100" fill="none" stroke="#9A6634" strokeWidth="1.5" />
      {/* the front flaps, folded open outward, until they close */}
      <motion.g {...timed({ opacity: [1, 1, 0, 0, 1] }, [0, 0.24, 0.32, 0.95, 1], { opacity: 0 })}>
        <polygon points="40,100 120,140 104,166 24,126" fill="#E0B07A" />
        <polygon points="120,140 200,100 216,126 136,166" fill="#C9935A" />
      </motion.g>
      {/* the box closed: its top, and the tape along it */}
      <motion.g style={{ transformOrigin: "120px 100px" }} {...timed({ opacity: [0, 0, 1, 1, 0], scale: [0.6, 0.6, 1, 1, 1] }, [0, 0.26, 0.34, 0.95, 1], { opacity: 1, scale: 1 })}>
        <polygon points="120,60 200,100 120,140 40,100" fill="#DDAA70" />
        <polygon points="72,84 152,124 168,116 88,76" fill="#E9D3A8" opacity="0.95" />
      </motion.g>
      {/* the waybill, stamped on the right face */}
      <motion.g style={{ transformOrigin: "160px 150px" }} {...timed({ opacity: [0, 0, 1, 1, 0], scale: [1.7, 1.7, 1, 1, 1] }, [0, 0.42, 0.5, 0.95, 1], { opacity: 1, scale: 1 })}>
        <g transform="translate(134 128) skewY(-26.565)">
          <rect x="0" y="12" width="52" height="38" rx="3" fill="#fff" stroke="#E5E7EB" strokeWidth="1" />
          <rect x="0" y="12" width="52" height="8" rx="2" fill="#D25C28" />
          {[4, 7, 9, 13, 15, 18, 22, 24, 27, 31, 33, 36, 40, 42, 45].map((x, i) => (
            <rect key={x} x={x + 1} y="24" width={i % 3 ? 1.4 : 2.4} height="14" fill="#111827" />
          ))}
          <rect x="5" y="42" width="30" height="2.5" rx="1" fill="#9CA3AF" />
        </g>
      </motion.g>
    </svg>
  )
}

/**
 * The couriers whose waybills a company makes from its orders here, its own account connected (Speedy, Econt,
 * Pigeon Express — store {shipping_carriers}): their logos in a row, each in a white card. A courier
 * added to the store shows up here by itself.
 */
function CourierLogos({ language }) {
  const [carriers, setCarriers] = useState(null)
  useEffect(() => {
    let gone = false
    ShippingCarriers(language)
      .then((res) => !gone && setCarriers(res?.carriers || []))
      .catch(() => !gone && setCarriers([]))
    return () => {
      gone = true
    }
  }, [language])
  if (!carriers) return <div className="h-[96px]" />
  return (
    <ul className="m-0 p-0 list-none flex flex-wrap items-center justify-center gap-3 md:gap-5" data-testid="landing-couriers-logos">
      {carriers.map((carrier) => (
        <li key={carrier.id} className="w-[150px] md:w-[200px] h-[80px] md:h-[100px] rounded-[22px] bg-paper shadow-[0_2px_24px_rgba(0,0,0,0.05)] flex items-center justify-center px-6" title={carrier.name}>
          {carrier.logo ? <img src={cmsUrl(carrier.logo)} alt={carrier.name} className="max-h-[44px] md:max-h-[52px] max-w-full object-contain" loading="lazy" /> : <span className="text-lg font-bold text-text">{carrier.name}</span>}
        </li>
      ))}
    </ul>
  )
}

// a euro banknote, small: the bills that float around "sales" in the title
function EuroBill({ className = "" }) {
  return (
    <svg viewBox="0 0 48 26" className={className} aria-hidden="true">
      <rect x="1" y="1" width="46" height="24" rx="3" fill="#8CC98A" stroke="#3E8E4B" strokeWidth="1.5" />
      <rect x="5" y="5" width="38" height="16" rx="2" fill="none" stroke="#3E8E4B" strokeWidth="1" strokeDasharray="2 2" opacity="0.6" />
      <circle cx="24" cy="13" r="7" fill="#E8F5E4" stroke="#3E8E4B" strokeWidth="1.2" />
      <text x="24" y="17.2" textAnchor="middle" fontSize="11" fontWeight="700" fill="#2F6E3A" fontFamily="Arial, sans-serif">€</text>
    </svg>
  )
}

// the bills circle the word on a flat ellipse round its middle — behind the letters, within the line's height,
// so the word and the lines above stay readable
const BILLS = 4
const ORBIT_SECONDS = 9
const ORBIT_STEPS = 48

/**
 * The word the title is about ("sales"), in red with a brush stroke drawn under it and euro bills orbiting it;
 * still for those who ask for less motion.
 */
function SalesWord({ children }) {
  const still = useReducedMotion()
  const ref = useRef(null)
  // the orbit's radii (px), from the word's size: wider than the word, a little taller than half its line
  const [orbit, setOrbit] = useState(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => {
      const fontSize = parseFloat(getComputedStyle(el).fontSize) || 40
      setOrbit({ rx: el.offsetWidth / 2 + fontSize * 0.45, ry: fontSize * 0.42, bill: fontSize * 0.62 })
    }
    measure()
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null
    observer?.observe(el)
    return () => observer?.disconnect()
  }, [])
  // a point of the ellipse at a share of the way round (0 on the right, going clockwise underneath first)
  const at = (share) => ({ x: Math.cos(share * 2 * Math.PI) * (orbit?.rx || 0), y: Math.sin(share * 2 * Math.PI) * (orbit?.ry || 0) })
  return (
    <span ref={ref} className="relative inline-block whitespace-nowrap" data-testid="landing-sales-word">
      <span className="relative z-10 text-[#D32F2F]">{children}</span>
      {/* the underline: a brush stroke, drawn in */}
      <svg viewBox="0 0 200 18" preserveAspectRatio="none" className="absolute left-[-2%] -bottom-[0.18em] w-[104%] h-[0.32em] z-10 overflow-visible" aria-hidden="true">
        <motion.path
          d="M3 12 C 40 4, 80 15, 120 8 S 180 6, 197 10"
          fill="none"
          stroke="#D32F2F"
          strokeWidth="5"
          strokeLinecap="round"
          initial={still ? false : { pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.9, delay: 0.3, ease: "easeOut" }}
        />
      </svg>
      {orbit &&
        Array.from({ length: BILLS }, (_, i) => {
          const start = i / BILLS
          const points = Array.from({ length: ORBIT_STEPS + 1 }, (_, k) => at(start + k / ORBIT_STEPS))
          return (
            <motion.span
              key={i}
              className="absolute left-1/2 top-1/2 pointer-events-none z-0"
              style={{ width: orbit.bill, marginLeft: -orbit.bill / 2, marginTop: -orbit.bill * 0.27 }}
              initial={still ? { ...points[0], opacity: 0.85 } : { ...points[0], opacity: 0 }}
              animate={still ? { ...points[0], opacity: 0.85 } : { x: points.map((p) => p.x), y: points.map((p) => p.y), opacity: 0.9, rotate: [-8, 8, -8] }}
              transition={still ? undefined : { x: { duration: ORBIT_SECONDS, repeat: Infinity, ease: "linear" }, y: { duration: ORBIT_SECONDS, repeat: Infinity, ease: "linear" }, rotate: { duration: 3 + i * 0.5, repeat: Infinity, ease: "easeInOut" }, opacity: { duration: 0.6, delay: 0.6 + i * 0.2 } }}
              aria-hidden="true"
            >
              <EuroBill className="w-full h-auto drop-shadow-sm" />
            </motion.span>
          )
        })}
    </span>
  )
}

/** "A new sales channel" — the word "sales" where the language puts it, as SalesWord. */
function SalesHeadline({ t }) {
  const [before, after = ""] = t`A new %s channel`.split("%s")
  return (
    <>
      {before}
      <SalesWord>{t`sales`}</SalesWord>
      {after}
    </>
  )
}

/** "New customers straight to your shop": the words before in the title's color, "your shop" in the brand's, where the language puts it. */
function ShopLine({ t }) {
  const [before, after = ""] = t`New customers straight to %s`.split("%s")
  return (
    <span className="block mt-4 md:mt-6">
      {before}
      <span className="text-primary">{t`your shop`}</span>
      {after}
    </span>
  )
}

function SectionTitle({ eyebrow, title, body, center = true }) {
  return (
    <div className={center ? "text-center max-w-[760px] mx-auto" : "max-w-[640px]"}>
      {eyebrow && <Typography 
        variant="label_upper_accent" 
        className="inline-block mb-3 text-primary"
      >
        {eyebrow}
      </Typography>
      }
      <Typography 
        component="h2"
        variant="h1" 
        className="leading-none mb-4"
      > 
        {title}
      </Typography>
      {body && <Typography variant="body1_dim">
        {body}
      </Typography>}
    </div>
  )
}

// ---------------------------------------------------------------- the page

// the example sale: a made-up brand's wireless headphones at 79 (in the store's
// currency, in cents), at one rate of the 7–25% range; the photo made with Gemini
const EXAMPLE_PRICE = 7900
const EXAMPLE_RATE = 10
const EXAMPLE_PHOTO = "/static/landing/products/headphones.webp"
const money = (symbol, cents) => `${symbol}${(cents / 100).toFixed(2)}`

export default function MerchantLanding({ onApply }) {
  const t = usePortalTexts(LANDING)
  const language = useSiteLanguage()
  const symbol = useMoneySymbol()
  const commissionCents = Math.round((EXAMPLE_PRICE * EXAMPLE_RATE) / 100)
  const mounted = useMounted()
  const figures = useMemo(sampleFigures, [])
  const signIn = () => PubSub.publish("signin", { redirect: "/vendor" })
  const apply = onApply || signIn

  const cta = (
    <div className="flex flex-wrap items-center gap-3">
      <Button 
        variant="primary_l" 
        onClick={apply} 
        data-testid="landing-apply"
      >
        {t`Start selling`} <ArrowRightIcon className="w-5 h-5 stroke-2" />
      </Button>
      <Button 
        variant="secondary_l" 
        onClick={signIn} 
        data-testid="landing-signin"
        // phones: Sign in sits in the top bar (Navbar), Start selling stands alone
        className="hidden lg:inline-flex lg:min-w-[160px]"
      >
        {t`Sign in`}
      </Button>
    </div>
  )

  return (
    <div className="w-full overflow-hidden" data-testid="merchant-landing">
      {/* ------------------------------------------------ hero */}
      <section className="relative w-full bg-gradient-to-b from-primary_lt/30 to-paper">
        <div className={`w-full ${CONTENT_WIDTH} grid grid-cols-1 [&>*]:min-w-0 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-14 items-center px-4 md:px-6 pt-10 md:pt-16 pb-14 md:pb-20 mx-auto`}>
          <div>
            <span className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-primary_lt/70 text-primary text-[14px] font-semibold">
              <SparklesIcon className="w-4 h-4" /> 
              {t`Aldoo for merchants`}
            </span>

            <Typography 
              variant="title"
              className="mb-5 leading-none"
            >
              <SalesHeadline t={t} />
              {/* on a line of its own, clear of the bills orbiting the word above */}
              <ShopLine t={t} />
            </Typography>

            <Typography 
              variant="body1_dim"
              className="max-w-[560px] !leading-relaxed mt-0 mb-7"
            >
              {t`Keep your shop as it is. Aldoo shows your catalogue in its Marketplace and sends you new orders.`}
            </Typography>
            
            <ul className="m-0 mb-8 p-0 list-none flex flex-col gap-2.5">
              {[
                t`No adding products by hand: we keep your catalogue for you.`,
                t`Buyers pay online or cash on delivery; we report the sales to you.`,
                t`Get the waybill automatically with each order.`,
              ].map((line) => (
                <li key={line} className="flex items-start gap-2.5 text-[15px] md:text-[16px] text-text">
                  <CheckIcon className="w-5 h-5 mt-0.5 shrink-0 text-primary" strokeWidth={2.5} />
                  {line}
                </li>
              ))}
            </ul>

            {cta}

            <Typography
              variant="txt1_dim"
              className="block mt-4 max-w-[560px] leading-tight" data-testid="landing-rate-note"
            >
              {t`Register now to get an offer for your rate.`}
            </Typography>
          </div>

          <HeroVisual figures={figures} t={t} mounted={mounted} />
        </div>

      </section>

      {/* ------------------------------------------------ a channel, not a replacement */}
      <section className="w-full py-16 md:py-24">
        <div className={`w-full ${CONTENT_WIDTH} mx-auto px-4 md:px-6`}>
          {/* one idea on a quiet stage, like the catalogue sync below: big type, much air, the picture */}
          <div className="rounded-[32px] bg-[#f5f5f7] px-5 md:px-16 py-14 md:py-24">
            <SectionTitle
              eyebrow={t`A sales channel`}
              title={<>{t`Your site keeps its customers.`}<br /><span className="text-primary">{t`Aldoo brings new ones.`}</span></>}
              body={t`Aldoo doesn't replace your shop. It's one more place you sell — to people who'd never find your website.`}
            />

            <ChannelFlow t={t} />
          </div>
        </div>
      </section>

      {/* ------------------------------------- the couriers we connect */}
      <section className="w-full py-16 md:py-24" data-testid="landing-couriers">
        <div className={`w-full ${CONTENT_WIDTH} mx-auto px-4 md:px-6`}>
          {/* one idea on a quiet stage: the parcel packing itself, the couriers, three facts */}
          <div className="rounded-[32px] bg-[#f5f5f7] px-5 md:px-16 py-14 md:py-24">
            <SectionTitle
              eyebrow={t`Couriers`}
              title={t`Already shipping with these couriers? We've got you covered.`}
              body={t`Connect your courier to your profile and get the waybill as soon as an order comes in.`}
            />
            <div className="mt-10 md:mt-14 flex justify-center">
              <PackingBox />
            </div>
            <div className="mt-10 md:mt-14">
              <CourierLogos language={language} />
            </div>
            {/* what a connected courier does, in three words each */}
            <div className="mt-10 md:mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[15px] md:text-[17px] font-semibold text-text_secondary">
              {[
                [DocumentTextIcon, t`A waybill at every order`],
                [TruckIcon, t`Your delivery prices`],
                [BanknotesIcon, t`Cash on delivery`],
              ].map(([Icon, label]) => (
                <span key={label} className="inline-flex items-center gap-2">
                  <Icon className="w-5 h-5 text-primary" strokeWidth={2} />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------ Catalogue import */}
      <section className="w-full pb-16 md:pb-24" data-testid="landing-import">
        <div className={`w-full ${CONTENT_WIDTH} mx-auto px-4 md:px-6`}>
          {/* one idea on a quiet stage: much air, big type, the picture, then three words */}
          <div className="rounded-[32px] bg-[#f5f5f7] px-5 md:px-16 py-14 md:py-24">
            <SectionTitle
              eyebrow={t`Catalogue import`}
              title={<>{t`We'll get your products onto the platform,`}<br /><span className="text-primary">{t`so you don't lose your valuable time.`}</span></>}
            />
            <ImportVisit t={t} />
            {/* what stays in sync: three big words, then how often */}
            <div className="mt-14 md:mt-20 grid grid-cols-3 gap-2.5 md:gap-5 max-w-[880px] mx-auto" data-testid="landing-sync">
              {[
                [PlusIcon, t`New products`],
                [TagIcon, t`Prices`],
                [CubeIcon, t`Stock`],
              ].map(([Icon, label]) => (
                <div key={label} className="min-w-0 flex flex-col items-center gap-3 md:gap-5 rounded-[22px] md:rounded-[28px] bg-paper px-2 md:px-6 py-6 md:py-10 shadow-[0_2px_24px_rgba(0,0,0,0.05)]">
                  <span className="w-11 h-11 md:w-16 md:h-16 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <Icon className="w-5 h-5 md:w-8 md:h-8" strokeWidth={2} />
                  </span>
                  <span className="max-w-full text-center text-[15px] md:text-[26px] font-bold tracking-tight leading-tight text-text">{label}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 md:mt-8 mb-0 flex items-center justify-center gap-2 text-[17px] md:text-[21px] font-semibold text-text_secondary">
              <ArrowPathIcon className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2} />
              {t`Synced every day.`}
            </p>
            {/* where the products come from: one quiet line */}
            <div className="mt-10 md:mt-14 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[14px] text-text_secondary">
              {[
                [GlobeAltIcon, t`Website`],
                [BuildingStorefrontIcon, t`Shop platform`],
                [DocumentTextIcon, t`Feed`],
                [CircleStackIcon, t`ERP`],
              ].map(([Icon, label]) => (
                <span key={label} className="inline-flex items-center gap-1.5">
                  <Icon className="w-4 h-4" />
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* -------------------------------------- inside the portal */}
      <section className="w-full py-16 md:py-24" data-testid="landing-portal-section">
        <div className={`w-full ${CONTENT_WIDTH} mx-auto px-4 md:px-6`}>
          <SectionTitle
            eyebrow={t`Merchant portal`}
            title={t`One place for your orders, products and payouts`}
            body={t`An example company's portal: orders, products, sales, couriers, team and payouts. Try the tiles and the search.`}
          />

          <div className="mt-10">
            <PopTilt>
              <PortalPreview t={t} />
            </PopTilt>
          </div>

          <Typography 
            variant="txt1_dim" 
            className="block mt-4 text-center"
          >
            {t`An example company with sample orders.`}
          </Typography>
        </div>
      </section>

      {/* ------------------------------------------------ Aldoo AI */}
      <section className="w-full py-16 md:py-24" data-testid="landing-advisor">
        <div className={`w-full ${CONTENT_WIDTH} mx-auto px-4 md:px-6 grid grid-cols-1 [&>*]:min-w-0 lg:grid-cols-[1fr_1.05fr] gap-10 lg:gap-16 items-center`}>
          <div>
            <SectionTitle
              center={false}
              eyebrow={ALDOO_ADVISOR.name}
              title={t`Ask your sales a question. They know best!`}
              body={t`Aldoo AI reads your sales and answers in plain words: what sells, when, and what to do next.`}
            />

            <ul className="m-0 mt-8 p-0 list-none flex flex-col gap-3">
              {[
                t`Your best sellers, by any period`,
                t`Your strongest days and hours`,
                t`Where your buyers come from`,
                t`Suggestions for what to promote, and when`,
              ].map((line) => (
                <li key={line} className="flex items-start gap-2.5 text-[15px] md:text-[16px] text-text">
                  <SparklesIcon className="w-5 h-5 mt-0.5 shrink-0 text-primary" />
                  {line}
                </li>
              ))}
            </ul>
          </div>
          <AdvisorChat t={t} />
        </div>
      </section>

      {/* ------------------------------------------------ commission */}
      {/* one sale at one rate, and all its commission buys: what the money is for, not a calculator */}
      <section className="w-full py-16 md:py-24">
        <div className={`w-full ${CONTENT_WIDTH} mx-auto px-4 md:px-6 grid grid-cols-1 [&>*]:min-w-0 lg:grid-cols-2 gap-10 lg:gap-16 items-center`}>
          <div>
            <SectionTitle
              center={false}
              eyebrow={t`Commission`}
              title={t`You pay only when you sell.`}
              body={t`One commission per sale. No monthly fee, no setup fee. No sale, no fee.`}
            />

            {/* the sale, worked out */}
            <div className="mt-8 p-5 md:p-6 soft_card" data-testid="landing-commission">
              <div className="flex items-center gap-4 mb-4">
                <img src={EXAMPLE_PHOTO} alt="" className="w-[64px] h-[64px] shrink-0 rounded-xl object-cover bg-[#f1efec] shadow-[0_8px_20px_rgba(0,0,0,0.10)]" loading="lazy" draggable={false} />
                <div className="min-w-0">
                  <span className="block text-[12px] uppercase tracking-wide text-text_secondary">{t`Example sale`}</span>
                  <span className="block text-[16px] font-bold leading-snug">{t`Aurel Audio · Wireless headphones`}</span>
                  <span className="block text-[13px] text-text_secondary">{t`Electronics`}</span>
                </div>
              </div>
              {[
                [t`The buyer pays`, money(symbol, EXAMPLE_PRICE), "text-text"],
                [t("Aldoo's commission (%s%%)", EXAMPLE_RATE), `− ${money(symbol, commissionCents)}`, "text-primary"],
              ].map(([label, value, tone]) => (
                <div key={label} className="flex items-center justify-between py-3 border-b border-bordered text-[15px]">
                  <span className="text-text_secondary">{label}</span>
                  <span className={`font-semibold tabular-nums ${tone}`}>{value}</span>
                </div>
              ))}
              <div className="flex items-center justify-between pt-4">
                <span className="text-[16px] font-bold">{t`Paid out to you`}</span>
                <span className="text-[28px] font-bold text-text tabular-nums" data-testid="landing-payout">{money(symbol, EXAMPLE_PRICE - commissionCents)}</span>
              </div>
            </div>
            <div className="mt-4 flex items-start gap-3 px-1">
              <ReceiptPercentIcon className="w-5 h-5 shrink-0 text-text_secondary" />
              <span className="text-[13px] text-text_secondary leading-snug" data-testid="landing-rate-note-example">{t`Example at 10%. Your rate, from 7% to 25%, depends on your catalogue and is set in your contract before you start.`}</span>
            </div>
          </div>

          {/* what that one commission buys: everything */}
          <div className="relative">
            <div className="absolute -inset-4 md:-inset-6 rounded-[32px] bg-gradient-to-br from-primary/15 via-[#fcda7d]/25 to-accent/10 blur-2xl" aria-hidden="true" />
            <div className="relative rounded-3xl bg-paper shadow-[0_24px_60px_rgba(0,0,0,0.12)] p-6 md:p-10" data-testid="landing-commission-includes">
              <div className="text-[30px] md:text-[40px] font-bold leading-[1.1] tracking-tight text-text">
                {t("For %s you get:", money(symbol, commissionCents)).split(money(symbol, commissionCents)).flatMap((part, i, parts) => (i < parts.length - 1 ? [part, <span key={i} className="text-primary tabular-nums">{money(symbol, commissionCents)}</span>] : [part]))}
              </div>
              <ul className="m-0 mt-8 p-0 list-none grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
                {[
                  [BuildingStorefrontIcon, t`Listing in Aldoo Marketplace`],
                  [MagnifyingGlassIcon, t`Search, categories and recommendations`],
                  [MegaphoneIcon, t`Ad campaigns on Aldoo and Google`],
                  [CreditCardIcon, t`Card payments and cash on delivery`],
                  [TruckIcon, t`Waybills with your couriers`],
                  [BanknotesIcon, t`Payouts, invoices and refunds`],
                  [ArrowPathIcon, t`Catalogue import and daily sync`],
                  [WrenchScrewdriverIcon, t`IT support for your catalogue`],
                  [ChartBarIcon, t`Dashboards and the AI advisor`],
                ].map(([Icon, line]) => (
                  <li key={line} className="flex items-center gap-3 text-[15px] leading-snug font-semibold text-text">
                    <span className="w-10 h-10 shrink-0 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary" />
                    </span>
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------ three steps */}
      {/* three steps, a few words each: the icon, the number, the step */}
      <section className="w-full py-16 md:py-24 bg-dark text-white" data-testid="landing-steps">
        <div className={`w-full ${CONTENT_WIDTH} mx-auto px-4 md:px-6`}>
          <div className="text-center max-w-[760px] mx-auto">
            <Typography variant="label_upper_accent" className="inline-block mb-3 text-primary">
              {t`How it works`}
            </Typography>
            <Typography component="h2" variant="h1" className="text-white leading-none my-0">
              {t`Three steps. That's all.`}
            </Typography>
          </div>

          <ol className="list-none grid grid-cols-1 [&>*]:min-w-0 md:grid-cols-3 gap-10 md:gap-8 p-0 m-0 mt-12 md:mt-16">
            {[
              [UserPlusIcon, t`Register`],
              [CloudArrowUpIcon, t`Point to your products`],
              [InboxArrowDownIcon, t`Start selling`],
            ].map(([Icon, title], i) => (
              <li key={title} className="relative flex flex-col items-center text-center">
                <span className="w-16 h-16 md:w-20 md:h-20 rounded-[22px] bg-primary text-white flex items-center justify-center shadow-[0_12px_32px_rgba(0,0,0,0.35)]">
                  <Icon className="w-8 h-8 md:w-10 md:h-10" />
                </span>
                <span className="mt-5 text-[13px] font-bold tracking-[0.2em] text-neutral_1 tabular-nums">0{i + 1}</span>
                <Typography component="h3" variant="h4" className="mt-1 mb-0 text-text_inv max-w-[260px]">
                  {title}
                </Typography>
                {/* the first step can be taken right here */}
                {i === 0 && (
                  <Button variant="primary" onClick={apply} className="mt-5" data-testid="landing-steps-offer">
                    {t`Get an offer`}
                  </Button>
                )}
                {i < 2 && <ArrowRightIcon className="hidden md:block absolute -right-6 top-8 md:top-10 -translate-y-1/2 w-6 h-6 text-theme_white/30" />}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ------------------------------- no shop yet: Aldoo is the shop */}
      <section className="w-full py-16 md:py-24" data-testid="landing-no-shop">
        <div className={`w-full ${CONTENT_WIDTH} mx-auto px-4 md:px-6`}>
          <div className="rounded-[28px] bg-dark text-white px-5 md:px-12 py-10 md:py-14 grid grid-cols-1 [&>*]:min-w-0 lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-14 items-center">
            <div>
              <Typography variant="label_upper_accent" className="inline-block mb-3 text-primary">
                {t`Products, but no shop?`}
              </Typography>

              <Typography 
                component="h2"
                variant="h1" 
                className="text-text_inv leading-none mb-4"
              >
                {t`No online shop yet?`}
                <br />
                <span className="text-primary">
                  {t`Aldoo is your shop.`}
                </span>
              </Typography>

              <Typography 
                variant="body1_dim" 
                className="text-neutral_2 my-6"
              >
                {t`Add your products and sell with Aldoo as your online store.`}
              </Typography>

              <ul className="m-0 mb-8 p-0 list-none flex flex-col gap-3">
                {[
                  [BuildingStorefrontIcon, t`A page for your company and its products in Aldoo Marketplace`],
                  [BanknotesIcon, t`Card payments, and payouts to your bank account`],
                  [ChartBarIcon, t`Sales statistics and dashboards`],
                  [DocumentTextIcon, t`Orders, invoices and refunds`],
                ].map(([Icon, line]) => (
                  <li key={line} className="flex items-center gap-3 text-[15px] md:text-[16px]">
                    <span className="w-9 h-9 shrink-0 rounded-lg bg-paper/10 text-primary flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </span>

                    <span className="flex-1 leading-tight">
                      {line}
                    </span>

                    <span className="shrink-0 px-2 py-0.5 rounded-full bg-success/20 text-success text-[12px] font-semibold uppercase tracking-wide">
                      {t`Free`}
                    </span>
                  </li>
                ))}
              </ul>

              {cta}
            </div>

            {/* what it costs: nothing, until something sells */}
            <div className="rounded-3xl bg-paper text-text p-6 md:p-8 shadow-[0_24px_60px_rgba(0,0,0,0.35)]" data-testid="landing-no-shop-costs">
              <Typography variant="label_upper_dim" className="text-sm  block mb-4">
                {t`What it costs you`}
              </Typography>
              {[
                t`Setup`,
                t`Monthly fee`,
                t`Storefront`,
                t`Payments and statistics`,
              ].map((label) => (
                <div key={label} className="flex items-center justify-between py-3 border-b border-bordered text-[15px]">
                  <span className="text-text_secondary">{label}</span>
                  <span className="font-bold text-text tabular-nums">€0</span>
                </div>
              ))}

              <div className="flex items-center justify-between gap-3 pt-4">
                <span className="flex flex-col">
                  <Typography variant="body1_accent">
                    {t`Commission on a sale`}
                  </Typography>
                </span>

                <span className="text-[26px] font-bold text-primary tabular-nums whitespace-nowrap">
                  7–25%
                </span>
              </div>

              <div className="flex items-center gap-3 bg-neutral rounded-xl p-3 mt-6">
                <CheckBadgeIcon className="w-6 h-6 shrink-0 text-success" />
                <Typography variant="txt1_dim" className="leading-tight">
                  {t`Nothing to pay until you sell: the commission is taken only from what sells.`}
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
